/**
 * The real clients, one per tree.
 *
 * Paths and payloads follow `gdsgate-saas`, docs/api.md; what the console still
 * expects and does not get is listed in docs/api-expectations.md.
 *
 * Three properties hold this file together, and each is a decision rather than
 * plumbing:
 *
 * **Same origin, and only that.** Both bases are absolute paths, never absolute
 * URLs, so every request goes to the host the page was served from. That is not
 * tidiness: the server checks `Origin` on every method that changes anything,
 * so a call to another host would be refused — and the session cookie carries
 * no `Domain`, so it would not be sent there in the first place. The
 * constructor refuses anything that is not a path, so this cannot be lost by
 * editing a default.
 *
 * **The session is a cookie the browser holds.** `credentials: 'same-origin'`
 * is what sends it. Nothing here reads, writes or stores a token: the cookie is
 * `HttpOnly`, script cannot see it, and there is nowhere in this repository a
 * token could be put.
 *
 * **Refusals are read by code.** The server's `code` decides what a screen
 * says; its `message` is shown only where it is the server's own particulars.
 */

import { ApiError, isServerErrorCode } from './errors';
import type { ApiErrorCode } from './errors';
import type { AccountApi, ClusterApi, ProviderApi, TenancyApi } from './client';
import type {
  AccountSession,
  AuditEvent,
  AuditFilter,
  ChangePasswordInput,
  ClusterNode,
  ClusterSession,
  ClusterSignInStart,
  IdentityChoice,
  IdentityFailure,
  InstallKey,
  InstallRole,
  JoinToken,
  NewTenantInput,
  OfferedResource,
  OfferedVersions,
  ReconcileRun,
  Resource,
  ResourceInput,
  Scope,
  RolloutBoard,
  RolloutWave,
  SignInCallback,
  SignInInput,
  SignInOffer,
  TenancyIdentity,
  Tenant,
  TenantUsage,
  Trail,
  UsagePeriod,
} from './types';

interface ErrorBody {
  error?: {
    code?: string;
    message?: string;
    fields?: Record<string, string>;
    /** Only `identity_provider_refused` carries one — see docs/api.md §5. */
    failure?: string;
  };
}

/** The server wraps a list in one key. Optional, so a changed answer degrades. */
interface TenantListBody {
  tenants?: Tenant[];
}

interface ScopesBody {
  scopes?: Scope[];
}

interface CatalogBody {
  resources?: Resource[];
}

/**
 * What a status means when the answer carried no code this console knows.
 *
 * A fallback, not the mapping: the code is the contract, and this only keeps an
 * unrecognised answer from being narrated as a network failure.
 */
function codeForStatus(status: number): ApiErrorCode {
  if (status === 401) return 'unauthenticated';
  if (status === 403) return 'forbidden';
  if (status === 429) return 'too_many_attempts';
  return 'unexpected';
}

/** `Retry-After`, when the deployment sends one. Seconds, or an HTTP date. */
function retryAfter(response: Response): number | undefined {
  const raw = response.headers.get('Retry-After');
  if (raw === null) return undefined;

  const seconds = Number(raw);
  if (raw.trim() !== '' && Number.isFinite(seconds) && seconds >= 0) return Math.round(seconds);

  const at = Date.parse(raw);
  if (Number.isNaN(at)) return undefined;
  return Math.max(0, Math.round((at - Date.now()) / 1000));
}

function refusal(response: Response, body: unknown): ApiError {
  const detail = (body as ErrorBody | null)?.error;
  const raw = detail?.code;
  const known = raw !== undefined && isServerErrorCode(raw);
  const wait = retryAfter(response);

  return new ApiError(known ? raw : codeForStatus(response.status), {
    status: response.status,
    ...(detail?.message === undefined ? {} : { message: detail.message }),
    ...(detail?.fields === undefined ? {} : { fields: detail.fields }),
    // Kept as it arrived. A code this console does not know reads as `unknown`
    // where it is turned into words, which is one place rather than here.
    ...(detail?.failure === undefined ? {} : { failure: detail.failure as IdentityFailure }),
    ...(wait === undefined ? {} : { retryAfterSeconds: wait }),
    ...(raw === undefined || known ? {} : { unknownCode: raw }),
  });
}

/** Shared plumbing. Neither client inherits the other's methods from it. */
class HttpTransport {
  readonly #base: string;

  constructor(base: string) {
    if (!base.startsWith('/')) {
      throw new Error('the API base has to be a same-origin path');
    }
    this.#base = base.replace(/\/$/, '');
  }

  async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    let response: Response;
    try {
      response = await fetch(`${this.#base}${path}`, {
        ...init,
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          ...(init.body === undefined ? {} : { 'Content-Type': 'application/json' }),
          ...init.headers,
        },
      });
    } catch {
      throw new ApiError('network');
    }

    if (response.status === 204) return undefined as T;

    const text = await response.text();
    let body: unknown = null;
    try {
      body = text === '' ? null : JSON.parse(text);
    } catch {
      // An answer that is not JSON did not come from this API — a proxy's own
      // error page is the usual cause. On a refusal there is still a status to
      // go on; on a success there is nothing to hand back.
      if (response.ok) throw new ApiError('network', { status: response.status });
    }

    if (!response.ok) throw refusal(response, body);
    return body as T;
  }
}

const json = (body: unknown): RequestInit => ({ body: JSON.stringify(body) });
const read = (signal?: AbortSignal): RequestInit => (signal ? { signal } : {});

// ---------------------------------------------------------------------------

export class HttpAccountApi implements AccountApi {
  readonly #http: HttpTransport;

  constructor(base = '/api/account') {
    this.#http = new HttpTransport(base);
  }

  getSession(signal?: AbortSignal): Promise<AccountSession | null> {
    return this.#http.request<AccountSession | null>('/session', read(signal));
  }

  signIn(input: SignInInput): Promise<AccountSession> {
    return this.#http.request<AccountSession>('/session', { method: 'POST', ...json(input) });
  }

  signOut(): Promise<void> {
    return this.#http.request<void>('/session', { method: 'DELETE' });
  }

  changePassword(input: ChangePasswordInput): Promise<void> {
    return this.#http.request<void>('/password', { method: 'POST', ...json(input) });
  }

  readonly provider: ProviderApi = {
    listTenants: async (signal) => {
      const body = await this.#http.request<TenantListBody>('/tenants', read(signal));
      return body.tenants ?? [];
    },

    getTenant: (slug: string, signal) =>
      this.#http.request<Tenant>(`/tenants/${encodeURIComponent(slug)}`, read(signal)),

    createTenant: (input: NewTenantInput) =>
      this.#http.request<Tenant>('/tenants', { method: 'POST', ...json(input) }),

    reconcile: (slug: string) =>
      this.#http.request<ReconcileRun>(`/tenants/${encodeURIComponent(slug)}/reconcile`, {
        method: 'POST',
      }),

    // Reading, and there is nothing beside it: no `PUT`, no `DELETE`, no method
    // on this surface that writes a catalogue at all.
    listResources: async (slug: string, signal) => {
      const body = await this.#http.request<CatalogBody>(
        `/tenants/${encodeURIComponent(slug)}/resources`,
        read(signal),
      );
      return body.resources ?? [];
    },

    // Both ends always travel, because the type has room for nothing less. The
    // server has no default for either, and a request that left one off would
    // be refused with `invalid_request` after somebody had already waited for
    // an answer.
    readUsage: (slug: string, period: UsagePeriod, signal) => {
      const window = new URLSearchParams({ from: period.from, to: period.to });
      return this.#http.request<TenantUsage>(
        `/tenants/${encodeURIComponent(slug)}/usage?${window.toString()}`,
        read(signal),
      );
    },

    readTrail: async (slug: string, signal) => {
      const body = await this.#http.request<Trail>(
        `/tenants/${encodeURIComponent(slug)}/audit`,
        read(signal),
      );
      return body.entries ?? [];
    },

    // One address for the board and for the four things that move it, because
    // all of them answer with the same board: after a move there is nothing
    // else worth reading, and a second read would show a moment later than the
    // one the answer described.
    readRollout: (signal) => this.#http.request<RolloutBoard>('/rollout', read(signal)),

    setWave: (slug: string, wave: RolloutWave) =>
      this.#http.request<RolloutBoard>(`/tenants/${encodeURIComponent(slug)}/wave`, {
        method: 'PUT',
        ...json({ wave }),
      }),

    // `202`: the first wave has begun moving and the work outlives the request.
    startRollout: (target: string) =>
      this.#http.request<RolloutBoard>('/rollout', { method: 'POST', ...json({ target }) }),

    advanceRollout: () =>
      this.#http.request<RolloutBoard>('/rollout/advance', { method: 'POST' }),

    offeredVersions: () => this.#http.request<OfferedVersions>('/versions'),

    // `202` as well, and no body: who is moved again follows from the steps.
    retryRollout: () => this.#http.request<RolloutBoard>('/rollout/retry', { method: 'POST' }),

    stopRollout: () => this.#http.request<RolloutBoard>('/rollout', { method: 'DELETE' }),
  };

  /** Note the paths: no tenancy segment. The server reads it off the session. */
  readonly tenancy: TenancyApi = {
    getIdentity: (signal) => this.#http.request<TenancyIdentity>('/tenancy/identity', read(signal)),

    chooseIdentity: (choice: IdentityChoice) =>
      this.#http.request<TenancyIdentity>('/tenancy/identity', { method: 'PUT', ...json(choice) }),

    applyIdentity: () =>
      this.#http.request<TenancyIdentity>('/tenancy/identity/apply', { method: 'POST' }),

    revertIdentity: () =>
      this.#http.request<TenancyIdentity>('/tenancy/identity/revert', { method: 'POST' }),

    dismissFailure: () =>
      this.#http.request<TenancyIdentity>('/tenancy/identity/failure', { method: 'DELETE' }),
  };
}

// ---------------------------------------------------------------------------

export class HttpClusterApi implements ClusterApi {
  readonly #http: HttpTransport;

  constructor(base = '/api/cluster') {
    this.#http = new HttpTransport(base);
  }

  getSession(signal?: AbortSignal): Promise<ClusterSession | null> {
    return this.#http.request<ClusterSession | null>('/session', read(signal));
  }

  signInOffer(signal?: AbortSignal): Promise<SignInOffer> {
    return this.#http.request<SignInOffer>('/sign-in/offer', read(signal));
  }

  /**
   * A `POST` with nothing to send, because it writes: a row on the server and a
   * short-lived cookie in this browser. Being a `POST` also puts it behind the
   * server's `Origin` check, so a sign-in cannot be started in somebody else's
   * browser from somebody else's page.
   */
  startSignIn(): Promise<ClusterSignInStart> {
    return this.#http.request<ClusterSignInStart>('/sign-in', { method: 'POST' });
  }

  /** Only `code` and `state`: the tenancy and the return address are the server's. */
  completeSignIn(callback: SignInCallback): Promise<ClusterSession> {
    return this.#http.request<ClusterSession>('/sign-in/complete', {
      method: 'POST',
      ...json({ code: callback.code, state: callback.state }),
    });
  }

  signOut(): Promise<void> {
    return this.#http.request<void>('/session', { method: 'DELETE' });
  }

  async listResources(signal?: AbortSignal): Promise<Resource[]> {
    const body = await this.#http.request<CatalogBody>('/resources', read(signal));
    return body.resources ?? [];
  }

  /**
   * The id travels in the path and the body carries the rest of the resource.
   *
   * The answer is not read: what the console needs after a write is the
   * catalogue, which it re-reads, and a `502` means the declaration stands
   * anyway — so there is nothing here that a body could tell it.
   */
  async listScopes(signal?: AbortSignal): Promise<Scope[]> {
    const body = await this.#http.request<ScopesBody>('/scopes', read(signal));
    return body.scopes ?? [];
  }

  /**
   * The prefix travels in the path and the body carries the rest, the way a
   * resource does. The answer is not read: what the console needs afterwards is
   * the listing, which it re-reads.
   */
  async putScope(prefix: string, input: Scope): Promise<void> {
    await this.#http.request<unknown>(`/scopes/${encodeURIComponent(prefix)}`, {
      method: 'PUT',
      ...json(input),
    });
  }

  async deleteScope(prefix: string): Promise<void> {
    await this.#http.request<void>(`/scopes/${encodeURIComponent(prefix)}`, {
      method: 'DELETE',
    });
  }

  async putResource(id: string, input: ResourceInput): Promise<void> {
    await this.#http.request<unknown>(`/resources/${encodeURIComponent(id)}`, {
      method: 'PUT',
      ...json(input),
    });
  }

  listNodes(signal?: AbortSignal): Promise<ClusterNode[]> {
    return this.#http
      .request<{ nodes: ClusterNode[] }>('/nodes', read(signal))
      .then((answer) => answer.nodes);
  }

  listOffered(signal?: AbortSignal): Promise<OfferedResource[]> {
    return this.#http
      .request<{ offered: OfferedResource[] }>('/offered', read(signal))
      .then((answer) => answer.offered);
  }

  listAudit(filter: AuditFilter = {}, signal?: AbortSignal): Promise<AuditEvent[]> {
    // Only what was asked for. An empty `actor=` would narrow the chain to the
    // person with no name, which is nobody — a filter nobody set has to be a
    // parameter nobody sends.
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(filter)) {
      if (value !== undefined && value !== '') query.set(key, String(value));
    }
    const suffix = query.size > 0 ? `?${query.toString()}` : '';
    return this.#http
      .request<{ events: AuditEvent[] }>(`/audit${suffix}`, read(signal))
      .then((answer) => answer.events);
  }

  /** A `POST` because it mints: the answer is a credential, and the only copy. */
  mintJoinToken(ttlSecs?: number): Promise<JoinToken> {
    return this.#http.request<JoinToken>('/nodes/token', {
      method: 'POST',
      ...json(ttlSecs === undefined ? {} : { ttl_secs: ttlSecs }),
    });
  }

  async listServing(signal?: AbortSignal): Promise<string[]> {
    const body = await this.#http.request<{ resources: string[] }>(
      '/serving',
      signal === undefined ? {} : { signal },
    );
    return body.resources;
  }

  mintInstallKey(role: InstallRole): Promise<InstallKey> {
    return this.#http.request<InstallKey>('/install-key', {
      method: 'POST',
      ...json({ role }),
    });
  }

  deleteNode(id: string): Promise<void> {
    return this.#http.request<void>(`/nodes/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  revokeNode(id: string): Promise<void> {
    return this.#http.request<void>(`/nodes/${encodeURIComponent(id)}/revoke`, {
      method: 'POST',
    });
  }

  deleteResource(id: string): Promise<void> {
    return this.#http.request<void>(`/resources/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }
}
